import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import DeliveryPrefs from './asset-delivery/DeliveryPrefs';
import './Assets.css';

/**
 * Guided instructions on a ticket: IT or the contractor tells the person at
 * the machine what to do, and sees whether it worked.
 *
 * Two people use this, and the SERVER says which this is:
 *
 *   may_send    whoever is fixing it - suggestion chips from the device's
 *               latest diagnostics, each opening the instruction filled in
 *               for that machine, editable before it goes
 *   may_answer  the person it is for - each instruction with "Done" and
 *               "I need help"
 *
 * WHO IT IS FOR, AND WHERE IT WENT (063). When the server offers more than
 * one person - the requester, the contractor on the ticket - the sender
 * chooses; each sent instruction then says who it was for and where it was
 * delivered ("Sent to chat · to-do"), which is the recipient's own standing
 * choice. That choice is made in the "How to reach me" card below the list,
 * shown to whoever an instruction here could be for.
 *
 * FROM THE DEVICE'S PAGE (`assetId` instead of `ticketId`): the same panel,
 * every instruction ever sent about the device, and a send that lands on
 * its open ticket or raises one - the server says which.
 *
 * Opening the ticket as the person it is for marks a new instruction seen.
 * Nobody marks one verified from here: Jarvis does that when the device's
 * next diagnostic no longer finds the problem.
 */
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const when = (t) => (t ? new Date(t).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }) : '');

const STATUS = {
  sent: { word: 'Sent', tone: 'info' },
  seen: { word: 'Seen', tone: 'info' },
  done: { word: 'Done — waiting to be confirmed', tone: 'success' },
  needs_help: { word: 'Needs help', tone: 'warning' },
  verified: { word: 'Verified by the device', tone: 'success' }
};

const CHANNEL_WORDS = { comment: 'On the ticket (and by email if they are away)', bell: 'In their bell', agent: 'As a notice on their computer' };
const ROLE_WORDS = { requester: 'raised the ticket', holder: 'has the device', contractor: 'contractor' };

export default function Instructions({ ticketId = null, assetId = null, authHeaders = null, title = 'Instructions' }) {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const authKey = authHeaders ? JSON.stringify(authHeaders) : '';
  const config = authKey ? { headers: JSON.parse(authKey) } : undefined;
  const path = assetId ? `/assets/${assetId}/instructions` : `/tickets/${ticketId}/instructions`;

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API_BASE}${path}`, authKey ? { headers: JSON.parse(authKey) } : undefined);
      setData(r.data);
      // The person it is for has now seen what was sent to them.
      if (r.data.may_answer) {
        const fresh = (r.data.instructions || []).filter((i) => i.status === 'sent');
        if (fresh.length) {
          await Promise.all(fresh.map((i) => axios.patch(`${API_BASE}/instructions/${i.id}`, { status: 'seen' }).catch(() => {})));
          const again = await axios.get(`${API_BASE}${path}`);
          setData(again.data);
        }
      }
    } catch {
      setData(null);
    }
  }, [path, authKey]);

  useEffect(() => { load(); }, [load]);

  if (!data) return null;
  const list = data.instructions || [];
  if (!list.length && !data.may_send && !data.delivery) return null;
  const recipients = data.recipients || [];

  const open = (from) => {
    setError('');
    setNote('');
    setDraft({
      template_id: from?.template_id || from?.id || null,
      finding: from?.finding || null,
      title: from?.title || '',
      steps: (from?.steps || ['']).join('\n'),
      channels: from?.channels || ['comment', 'bell', 'agent'],
      to: recipients[0]?.key || null
    });
  };

  const send = async () => {
    setBusy(true);
    setError('');
    try {
      const r = await axios.post(`${API_BASE}${path}`, {
        template_id: draft.template_id, finding: draft.finding, title: draft.title.trim(),
        steps: draft.steps.split('\n').map((s) => s.trim()).filter(Boolean), channels: draft.channels,
        ...(recipients.length ? { to: draft.to } : {})
      }, config);
      setDraft(null);
      const went = (r.data?.sent_to || []).join(' · ');
      setNote([
        r.data?.ticket_raised ? `Ticket ${r.data.ticket_number} raised for it.` : (assetId && r.data?.ticket_number ? `On ticket ${r.data.ticket_number}.` : ''),
        went ? `Sent to ${went}.` : ''
      ].filter(Boolean).join(' '));
      await load();
    } catch (e) {
      setError(e.response?.data?.error || 'Could not send it.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="as-panel as-diag" style={{ marginBottom: '1rem' }} data-testid="instructions">
      <div className="as-diag-head">
        <h2>{title}</h2>
        {data.may_send && !draft && (
          <button type="button" className="btn btn-secondary" onClick={() => open(null)}>Send an instruction</button>
        )}
      </div>

      {data.may_send && !draft && (data.suggestions || []).length > 0 && (
        <div className="as-instr-suggest">
          <p className="as-quiet">From the device’s latest checks:</p>
          <div className="as-instr-chips">
            {data.suggestions.map((s) => (
              <button key={s.finding} type="button" className="as-instr-chip" onClick={() => open(s)}>{s.summary}</button>
            ))}
          </div>
        </div>
      )}

      {draft && (
        <Draft draft={draft} setDraft={setDraft} templates={data.templates || []} ownWords={data.may_write_own}
          recipients={recipients} fromDevice={Boolean(assetId)} openTicket={data.open_ticket_id}
          error={error} busy={busy} onSend={send} onCancel={() => { setDraft(null); setError(''); }} />
      )}
      {note && !draft && <p className="as-note" data-tone="info" data-testid="instruction-sent-note">{note}</p>}

      {!list.length && !draft && data.may_send && <p className="as-quiet">{assetId ? 'Nothing has been sent about this device yet.' : 'Nothing has been sent on this ticket yet.'}</p>}
      {list.map((i) => (
        <Instruction key={i.id} i={i} answer={data.may_answer} showTicket={Boolean(assetId)} onChanged={load} />
      ))}

      {data.delivery && <DeliveryPrefs initial={data.delivery} authHeaders={authHeaders} />}
    </section>
  );
}

function Draft({ draft, setDraft, templates, ownWords, recipients, fromDevice, openTicket, error, busy, onSend, onCancel }) {
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const pick = (id) => {
    const t = templates.find((x) => x.id === id);
    if (!t) { setDraft((d) => ({ ...d, template_id: null, finding: null })); return; }
    setDraft((d) => ({ ...d, template_id: t.id, finding: t.finding, title: t.title, steps: t.steps.join('\n'), channels: t.channels }));
  };
  const toggle = (c) => set('channels', draft.channels.includes(c) ? draft.channels.filter((x) => x !== c) : [...draft.channels, c]);
  const needsTo = recipients.length > 0;
  const ready = draft.title.trim() && draft.steps.trim() && draft.channels.length && (draft.template_id || ownWords)
    && (!needsTo || draft.to);
  return (
    <div className="as-diag-ask">
      {fromDevice && (
        <p className="as-quiet">
          {openTicket
            ? 'Kept with the device’s open ticket.'
            : 'The device has no open ticket, so Jarvis will raise one for this instruction and keep it there.'}
        </p>
      )}
      {needsTo && (
        <div className="form-group">
          <label htmlFor="in-to">For</label>
          <select id="in-to" value={draft.to || ''} onChange={(e) => set('to', e.target.value)}>
            {!draft.to && <option value="">Choose…</option>}
            {recipients.map((p) => (
              <option key={p.key} value={p.key}>{`${p.name}${ROLE_WORDS[p.role] ? ` — ${ROLE_WORDS[p.role]}` : ''}`}</option>
            ))}
          </select>
        </div>
      )}
      <div className="form-group">
        <label htmlFor="in-template">Instruction</label>
        <select id="in-template" value={draft.template_id || ''} onChange={(e) => pick(e.target.value)}>
          {ownWords ? <option value="">My own words</option> : <option value="" disabled>Choose one</option>}
          {templates.map((t) => <option key={t.id} value={t.id}>{t.title}</option>)}
        </select>
      </div>
      <div className="form-group">
        <label htmlFor="in-title">Title</label>
        <input id="in-title" value={draft.title} maxLength={120} onChange={(e) => set('title', e.target.value)} />
      </div>
      <div className="form-group">
        <label htmlFor="in-steps">Steps, one per line</label>
        <textarea id="in-steps" rows={6} value={draft.steps} onChange={(e) => set('steps', e.target.value)} />
      </div>
      <fieldset className="as-instr-channels">
        <legend>Send it</legend>
        {Object.entries(CHANNEL_WORDS).map(([c, w]) => (
          <label key={c} className="as-check">
            <input type="checkbox" checked={draft.channels.includes(c)} onChange={() => toggle(c)} />
            <span>{w}</span>
          </label>
        ))}
        {needsTo && (
          <p className="as-quiet" style={{ margin: '0.2rem 0 0', fontSize: '0.82rem' }}>
            And wherever they have asked to be reached — chat, the ticket, email or a to-do — which Jarvis reports once it is sent.
          </p>
        )}
      </fieldset>
      <p className="as-quiet">
        Instructions never ask anybody to switch off security, type a password or run a command; Jarvis refuses wording that does.
      </p>
      {error && <p className="as-error" role="alert">{error}</p>}
      <div className="as-actions">
        <button type="button" className="btn btn-primary" disabled={!ready || busy} onClick={onSend}>
          {busy ? 'Sending…' : 'Send'}
        </button>
        <button type="button" className="btn btn-secondary" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

function Instruction({ i, answer, showTicket, onChanged }) {
  const [helping, setHelping] = useState(false);
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const s = STATUS[i.status] || STATUS.sent;
  const mark = async (status) => {
    setError('');
    try {
      await axios.patch(`${API_BASE}/instructions/${i.id}`, { status, note: status === 'needs_help' ? note : undefined });
      setHelping(false);
      setNote('');
      onChanged();
    } catch (e) {
      setError(e.response?.data?.error || 'That did not work.');
    }
  };
  const sentTo = Array.isArray(i.sent_to) ? i.sent_to : null;
  return (
    <div className="as-diag-request" data-testid="instruction">
      <div className="as-diag-summary as-instr-row">
        <span className="as-chip" data-tone={s.tone}>{s.word}</span>
        <strong>{i.title}</strong>
        <span className="as-sub">
          {`Sent by ${i.sent_by_name || 'IT'}${i.by_contractor ? ' (contractor)' : ''}, ${when(i.sent_at)}`}
          {i.recipient_name ? ` · for ${i.recipient_name}` : ''}
          {showTicket && i.ticket_number ? ` · ${i.ticket_number}` : ''}
          {i.verified_at ? ` · confirmed ${when(i.verified_at)}` : ''}
          {sentTo && sentTo.length > 0 && (
            <>{' · '}<span className="as-sent-to" data-testid="sent-to">{`Sent to ${sentTo.join(' · ')}`}</span></>
          )}
        </span>
      </div>
      <ol className="as-instr-steps">{i.steps.map((x, n) => <li key={n}>{x}</li>)}</ol>
      {i.status === 'needs_help' && i.help_note && <p className="as-note" data-tone="warning">{`“${i.help_note}”`}</p>}
      {answer && i.status !== 'verified' && (
        <>
          {!helping && (
            <div className="as-actions">
              {i.status !== 'done' && (
                <button type="button" className="btn btn-primary btn-small" onClick={() => mark('done')}>Done</button>
              )}
              <button type="button" className="btn btn-secondary btn-small" onClick={() => setHelping(true)}>I need help</button>
            </div>
          )}
          {helping && (
            <div className="as-inline-form">
              <div className="form-group" style={{ flex: '1 1 16rem' }}>
                <label htmlFor={`help-${i.id}`}>What happened?</label>
                <input id={`help-${i.id}`} value={note} maxLength={1000} onChange={(e) => setNote(e.target.value)} />
              </div>
              <button type="button" className="btn btn-primary btn-small" disabled={!note.trim()} onClick={() => mark('needs_help')}>Send</button>
              <button type="button" className="btn btn-secondary btn-small" onClick={() => setHelping(false)}>Cancel</button>
            </div>
          )}
          {error && <p className="as-error" role="alert">{error}</p>}
        </>
      )}
    </div>
  );
}
